import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Page, Panel, Field, Kpi } from "../components/Layout";
import { api, notifyErr, fmtBytes } from "../lib/api";

const CAT = { audio: "Copie audio acquisite", temporary: "File temporanei", transcriptions: "Trascrizioni", analysis: "Analisi",
  reports_internal: "Report interni", reports_delivery: "Report consegna" };

export default function Archivio() {
  const [o, setO] = useState(null);
  const [target, setTarget] = useState(null);
  const [cats, setCats] = useState(["audio", "temporary"]);
  const [days, setDays] = useState("");
  const [plan, setPlan] = useState(null);
  const [confirm, setConfirm] = useState("");
  const load = () => api.get("/storage/overview").then((r) => setO(r.data)).catch(notifyErr);
  useEffect(() => { load(); }, []);
  const body = () => ({ customer_id: target.customer_id, categories: cats, older_than_days: days === "" ? null : Number(days) });
  const preview = async () => { try { setPlan((await api.post("/storage/cleanup/preview", body())).data); } catch (e) { notifyErr(e); } };
  const execute = async () => {
    try {
      const r = (await api.post("/storage/cleanup/execute", { ...body(), confirm })).data;
      toast.success(`${r.deleted} file eliminati · ${fmtBytes(r.freed_bytes)} liberati`);
      setTarget(null); setPlan(null); setConfirm(""); load();
    } catch (e) { notifyErr(e); }
  };
  const d = o?.disk;
  return (
    <Page title="Gestione Archivio" subtitle="Solo l'archivio SALES RESCUE viene modificato: le registrazioni originali sui server AlFa Dial non vengono mai toccate." testid="archivio-page">
      {d && <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Kpi label="Spazio totale" value={fmtBytes(d.total)} testid="disk-total" />
        <Kpi label="Spazio usato" value={fmtBytes(d.used)} testid="disk-used" />
        <Kpi label="Spazio libero" value={fmtBytes(d.free)} testid="disk-free" accent />
        <div className="bg-white border rounded-sm p-4"><div className="eyebrow">Utilizzo disco</div><Progress value={(d.used / d.total) * 100} className="h-2 mt-4 rounded-none" /><div className="text-xs mt-2">{Math.round((d.used / d.total) * 100)}%</div></div>
      </div>}
      <div className="bg-white border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Cliente</TableHead>{Object.values(CAT).map((l) => <TableHead key={l} className="text-right">{l}</TableHead>)}<TableHead className="text-right">Totale</TableHead><TableHead>Auto</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {o?.customers.map((c) => (
              <TableRow key={c.customer_id} className="row-hover text-xs" data-testid={`storage-row-${c.customer_id}`}>
                <TableCell className="font-medium text-sm">{c.name}<div className="text-neutral-500">{c.audio_files} file audio</div></TableCell>
                {Object.keys(CAT).map((k) => <TableCell key={k} className="text-right tabular-nums">{fmtBytes(c.usage[k])}</TableCell>)}
                <TableCell className="text-right tabular-nums font-medium">{fmtBytes(c.total)}</TableCell>
                <TableCell>{c.auto_cleanup ? "Sì" : "No"}</TableCell>
                <TableCell><Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => { setTarget(c); setPlan(null); setConfirm(""); }} data-testid={`cleanup-btn-${c.customer_id}`}>Pulizia</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Panel title="Regole" className="mt-4"><p className="text-xs text-neutral-600">I periodi di conservazione si configurano per cliente in <b>Clienti e Predictive</b>. Sono sempre esclusi dalla pulizia: file usati da job attivi e opportunità in attesa di revisione umana. La pulizia automatica (se attiva) viene eseguita ogni ora dal worker.</p></Panel>
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cleanup-dialog">
          <DialogHeader><DialogTitle>Pulizia archivio · {target?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(CAT).map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={cats.includes(k)} onCheckedChange={() => setCats(cats.includes(k) ? cats.filter((x) => x !== k) : [...cats, k])} data-testid={`cleanup-cat-${k}`} />{l}</label>
            ))}
          </div>
          <Field label="Più vecchi di (giorni)" hint="Vuoto = usa la conservazione configurata per il cliente; 0 = tutti i file"><Input type="number" min={0} value={days} onChange={(e) => setDays(e.target.value)} className="h-8 rounded-sm w-40" data-testid="cleanup-days-input" /></Field>
          <Button variant="outline" className="rounded-sm h-8 w-fit" onClick={preview} disabled={!cats.length} data-testid="cleanup-preview-btn">Anteprima</Button>
          {plan && (
            <div className="space-y-2" data-testid="cleanup-preview">
              <div className="text-sm"><b>{plan.count}</b> file da eliminare · <b>{fmtBytes(plan.total_bytes)}</b> · {plan.protected.length} protetti</div>
              <div className="border rounded-sm max-h-48 overflow-y-auto text-[11px] font-mono divide-y">
                {plan.files.map((f) => <div key={f.path} className="px-2 py-1 flex justify-between"><span className="truncate">{f.path}</span><span>{fmtBytes(f.size)}</span></div>)}
                {plan.protected.map((f) => <div key={f.path} className="px-2 py-1 flex justify-between text-amber-700"><span className="truncate">{f.path}</span><span>protetto: {f.reason}</span></div>)}
              </div>
              {plan.count > 0 && <Field label="Digitare ELIMINA per confermare"><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-8 rounded-sm w-40 font-mono" data-testid="cleanup-confirm-input" /></Field>}
            </div>
          )}
          <DialogFooter><Button className="rounded-sm bg-red-600 hover:bg-red-700" disabled={!plan?.count || confirm !== "ELIMINA"} onClick={execute} data-testid="cleanup-execute-btn">Elimina definitivamente</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
